import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Autocomplete, TextField, createFilterOptions } from '@mui/material';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY, ALL_UNITS_QUERY, CREATE_INVENTORY_ITEM_MUTATION } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import TrimNameInput from './TrimNameInput';

interface InventoryItem {
  id: number;
  name: string;
  inventoryType: string;
  costCents: number;
  archivedAt: string | null;
  widthInches?: number | null;
}

interface Unit {
  id: number;
  name: string;
  abbreviation: string;
}

interface NewInventoryOption {
  inputValue: string;
  name: string;
  isNew: true;
}

type InventoryOption = InventoryItem | NewInventoryOption;

const filterOptions = createFilterOptions<InventoryOption>();

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// Shirred Valance — same "Draperies"-family fabric/lining inputs as Roman/
// Pleated Valance: only one trim placement (Along Bottom — see
// Quoting_markdown/valances.md §3) and no Flap Size field (not part of this
// SOP; the work order outputs are Finished Width/Length, Hardware, and
// Returns only). Length is always derived server-side (20" IM, or 20" +
// Space Above Window for OM — see shirredValanceCalculations.ts), never
// collected here. Fabric Width is user-editable (defaults to 54").
//
// Unlike every other item type, this form's values are submitted through
// dedicated addShirredValanceDraftQuoteObject/updateShirredValanceDraftQuoteObject
// mutations (see DraftQuoteDetail.tsx's handleAddObject branch) rather than
// the generic addDraftQuoteObject/updateDraftQuoteObject every sibling type
// shares — a deliberate deviation requested for this type only. getValues()'s
// return shape still needs to match what buildInitialValuesFromObject
// (DraftQuoteDetail.tsx) exposes, since "go back and edit" round-trips
// through those same field names regardless of which mutation saved them.
const ShirredValanceForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;
  const [width, setWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [mountType, setMountType] = useState<'inside' | 'outside' | ''>(iv?.mountType || '');
  const [spaceAboveWindowInches, setSpaceAboveWindowInches] = useState(iv?.spaceAboveWindowInches != null ? String(iv.spaceAboveWindowInches) : '');
  const [fabricWidthInches, setFabricWidthInches] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [inventoryItemId, setInventoryItemId] = useState(iv?.inventoryItemId != null ? String(iv.inventoryItemId) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricRepeatInches, setFabricRepeatInches] = useState(iv?.fabricRepeatInches != null ? String(iv.fabricRepeatInches) : '');
  const [liningType, setLiningType] = useState<'standard' | 'blackout' | 'sheer' | 'none' | ''>(iv?.liningType || 'standard');
  const [interliningType, setInterliningType] = useState<'none' | 'flannel' | ''>(iv?.interliningType === 'flannel' ? 'flannel' : 'none');
  const [trimPlacement, setTrimPlacement] = useState<'none' | 'bottom' | ''>(iv?.trimPlacement === 'bottom' ? 'bottom' : 'none');
  const [trimDescription, setTrimDescription] = useState<string>(iv?.trimDescription || '');
  const [error, setError] = useState<string | null>(null);
  // Tracks the fabric name we last wrote into fabricDescription automatically,
  // so switching inventory items can update it without clobbering a hand-typed value.
  const [autoFilledDescription, setAutoFilledDescription] = useState<string | null>(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [pendingCost, setPendingCost] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data, refetch } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
    fetchPolicy: 'network-only',
  });
  const { data: unitsData } = useQuery(ALL_UNITS_QUERY);
  const [createInventoryItem] = useMutation(CREATE_INVENTORY_ITEM_MUTATION);

  const inventoryItems: InventoryItem[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: InventoryItem) => !i.archivedAt && i.inventoryType === 'FABRIC');
  const selectedInventoryItem = inventoryItems.find((i) => String(i.id) === inventoryItemId) || null;

  const units: Unit[] = unitsData?.allUnits?.nodes || [];
  const yardUnit = units.find((u) => /yard/i.test(u.name) || /^yd/i.test(u.abbreviation));

  // On mount with an existing inventory item selected, treat a description
  // that still matches the item's name as auto-filled so switching items later updates it.
  useEffect(() => {
    if (autoFilledDescription === null && selectedInventoryItem && fabricDescription.trim() === selectedInventoryItem.name) {
      setAutoFilledDescription(selectedInventoryItem.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInventoryItem]);

  const selectInventoryItem = (item: InventoryItem) => {
    setInventoryItemId(String(item.id));
    // Auto-fill the fabric description from the catalog name — but only if it's
    // still blank or still matches the previously auto-filled name, so we never
    // clobber something the user typed or edited by hand.
    setFabricDescription((prev) => {
      const isBlank = !prev.trim();
      const matchesPreviousAutoFill = autoFilledDescription !== null && prev === autoFilledDescription;
      return isBlank || matchesPreviousAutoFill ? item.name : prev;
    });
    setAutoFilledDescription(item.name);
    if (item.widthInches) {
      setFabricWidthInches(String(item.widthInches));
    }
  };

  const openCreateDialog = (name: string) => {
    setPendingName(name);
    setPendingCost('');
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCreateConfirm = async () => {
    if (!user?.domain_id || !pendingName.trim()) return;
    if (!yardUnit) {
      setCreateError('No "Yards" unit found — add one in Settings first.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const costCents = Math.round((parseFloat(pendingCost) || 0) * 100);
      const result = await createInventoryItem({
        variables: {
          domainId: user.domain_id,
          name: pendingName.trim(),
          inventoryType: 'FABRIC',
          unitId: yardUnit.id,
          costCents,
          description: null,
          widthInches: null,
          quantityOnHand: 0,
        },
      });
      const newItem = result.data?.createInventoryItem?.inventoryItem;
      await refetch();
      if (newItem?.id) {
        const newName = pendingName.trim();
        setInventoryItemId(String(newItem.id));
        setFabricDescription((prev) => {
          const isBlank = !prev.trim();
          const matchesPreviousAutoFill = autoFilledDescription !== null && prev === autoFilledDescription;
          return isBlank || matchesPreviousAutoFill ? newName : prev;
        });
        setAutoFilledDescription(newName);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create inventory item');
    } finally {
      setCreating(false);
    }
  };

  useImperativeHandle(ref, () => ({
    getValues: () => {
      const w = parseFloat(width);
      if (!w || w <= 0) {
        setError('Width must be greater than 0');
        return null;
      }
      if (!mountType) {
        setError('Mount type is required');
        return null;
      }
      setError(null);
      return {
        widthInches: w,
        mountType,
        spaceAboveWindowInches: mountType === 'outside' && spaceAboveWindowInches ? parseFloat(spaceAboveWindowInches) : null,
        fabricWidthInches: fabricWidthInches ? parseFloat(fabricWidthInches) : 54,
        inventoryItemId: inventoryItemId ? parseInt(inventoryItemId, 10) : null,
        fabricDescription: fabricDescription.trim() || null,
        fabricRepeatInches: fabricRepeatInches ? parseFloat(fabricRepeatInches) : null,
        liningType: liningType || 'standard',
        interliningType: interliningType || 'none',
        trimPlacement: trimPlacement || 'none',
        trimDescription: trimDescription.trim() || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Mount Type</label>
          <select
            className="input"
            value={mountType}
            onChange={(e) => setMountType(e.target.value as 'inside' | 'outside' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="inside">Inside Mount (IM)</option>
            <option value="outside">Outside Mount (OM)</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Width (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        {mountType === 'outside' && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Space Above Window (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <input
              type="number"
              min="0"
              step="0.25"
              className="input"
              value={spaceAboveWindowInches}
              onChange={(e) => setSpaceAboveWindowInches(e.target.value)}
              disabled={props.disabled}
            />
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Lining</label>
          <select
            className="input"
            value={liningType}
            onChange={(e) => setLiningType(e.target.value as 'standard' | 'blackout' | 'sheer' | 'none' | '')}
            disabled={props.disabled}
          >
            <option value="standard">Regular ($12.95/yd)</option>
            <option value="blackout">Blackout ($35.95/yd)</option>
            <option value="sheer">Sheer ($21.95/yd)</option>
            <option value="none">None</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Interlining</label>
          <select
            className="input"
            value={interliningType}
            onChange={(e) => setInterliningType(e.target.value as 'none' | 'flannel' | '')}
            disabled={props.disabled}
          >
            <option value="none">None</option>
            <option value="flannel">Flannel ($16.95/yd)</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Trim</label>
          <select
            className="input"
            value={trimPlacement}
            onChange={(e) => setTrimPlacement(e.target.value as 'none' | 'bottom' | '')}
            disabled={props.disabled}
          >
            <option value="none">None</option>
            <option value="bottom">Along Bottom</option>
          </select>
        </div>
        <TrimNameInput value={trimDescription} onChange={setTrimDescription} disabled={props.disabled} />
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Width (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={fabricWidthInches}
            onChange={(e) => setFabricWidthInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Repeat (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            placeholder="Pattern repeat, if any"
            value={fabricRepeatInches}
            onChange={(e) => setFabricRepeatInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 2 }}>
          <label>Inventory Item <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <Autocomplete<InventoryOption>
            size="small"
            disabled={props.disabled}
            options={inventoryItems}
            value={selectedInventoryItem}
            isOptionEqualToValue={(option, value) => (
              !('isNew' in option) && !('isNew' in value) && option.id === value.id
            )}
            getOptionLabel={(option) => (
              typeof option === 'string' ? option : ('isNew' in option ? option.inputValue : option.name)
            )}
            filterOptions={(options, params) => {
              const filtered = filterOptions(options, params);
              const inputValue = params.inputValue.trim();
              const exists = options.some((o) => !('isNew' in o) && o.name.toLowerCase() === inputValue.toLowerCase());
              if (inputValue !== '' && !exists) {
                filtered.push({ inputValue, name: inputValue, isNew: true });
              }
              return filtered;
            }}
            renderOption={(liProps, option) => (
              <li {...liProps} key={'isNew' in option ? '__new__' : option.id}>
                {'isNew' in option ? `+ Add "${option.inputValue}" as a new fabric` : option.name}
              </li>
            )}
            onChange={(_event, newValue) => {
              if (newValue == null) {
                setInventoryItemId('');
              } else if ('isNew' in newValue) {
                openCreateDialog(newValue.inputValue);
              } else {
                selectInventoryItem(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search fabric..." />
            )}
          />
        </div>
        {selectedInventoryItem && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Price</label>
            <div style={{ padding: '9px 0', fontSize: 14, color: '#111827' }}>
              {formatCost(selectedInventoryItem.costCents)}
            </div>
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="text"
            className="input"
            placeholder="e.g., Schumacher Tiger Palm - Cocoa"
            value={fabricDescription}
            onChange={(e) => setFabricDescription(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {createDialogOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 8, width: 420, maxWidth: '90%', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>New Fabric</h2>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>
                &ldquo;{pendingName}&rdquo; isn&apos;t in the inventory yet. Add it as a new fabric?
              </p>
            </div>

            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {createError && (
                <div style={{ padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 6, fontSize: 13 }}>
                  {createError}
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Name
                </label>
                <input
                  type="text"
                  className="input"
                  value={pendingName}
                  onChange={(e) => setPendingName(e.target.value)}
                  disabled={creating}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Cost per Yard ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input"
                  placeholder="0.00"
                  value={pendingCost}
                  onChange={(e) => setPendingCost(e.target.value)}
                  disabled={creating}
                />
              </div>

              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                Type: Fabric · Unit: {yardUnit ? yardUnit.name : 'Yards (not found)'} · Qty on hand: 0
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
              <button
                style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={() => setCreateDialogOpen(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={handleCreateConfirm}
                disabled={creating || !pendingName.trim()}
              >
                {creating ? 'Creating...' : 'Create Fabric'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ShirredValanceForm;
